import React, { useState } from 'react';
import { X, MapPin, Eye, EyeOff } from 'lucide-react';
import './CreateVendorModal.css';
import { API_BASE_URL } from '../../api';

const CreateVendorModal = ({ isOpen, onClose, onVendorCreated }) => {
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    email: '',
    phone: '',
    shopAddress: '',
    latitude: '',
    longitude: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.shopName.trim()) {
      newErrors.shopName = 'Shop name is required';
    }
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.shopAddress.trim()) {
      newErrors.shopAddress = 'Shop address is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Optional field validation
    if (formData.latitude && (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to retrieve your location. Please enter coordinates manually.');
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('adminToken') ? { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` } : {}),
          ...((localStorage.getItem('ADMIN_API_KEY') || localStorage.getItem('adminApiKey')) ? { 'x-admin-key': (localStorage.getItem('ADMIN_API_KEY') || localStorage.getItem('adminApiKey')) } : {})
        },
        body: JSON.stringify({
          shopName: formData.shopName.trim(),
          ownerName: formData.ownerName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.replace(/\D/g, ''),
          shopAddress: formData.shopAddress.trim(),
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Reset form
        setFormData({
          shopName: '',
          ownerName: '',
          email: '',
          phone: '',
          shopAddress: '',
          latitude: '',
          longitude: '',
          password: '',
          confirmPassword: ''
        });
        setErrors({});
        
        // Notify parent component
        onVendorCreated(data.vendor);
        
        // Show success message
        alert('Vendor account created successfully!');
        
        // Close modal
        onClose();
      } else {
        throw new Error(data.error || 'Failed to create vendor');
      }
    } catch (error) {
      console.error('Error creating vendor:', error);
      alert(error.message || 'Failed to create vendor account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        shopName: '',
        ownerName: '',
        email: '',
        phone: '',
        shopAddress: '',
        latitude: '',
        longitude: '',
        password: '',
        confirmPassword: ''
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-vendor-modal-overlay">
      <div className="create-vendor-modal">
        <div className="create-vendor-modal-header">
          <h2>Create Vendor Account</h2>
          <button 
            className="create-vendor-modal-close" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-vendor-form">
          <div className="create-vendor-form-grid">
            {/* Shop Name */}
            <div className="create-vendor-form-group">
              <label htmlFor="shopName" className="create-vendor-label">
                Shop Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="shopName"
                name="shopName"
                value={formData.shopName}
                onChange={handleInputChange}
                placeholder="Enter your shop name"
                className={`create-vendor-input ${errors.shopName ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.shopName && <span className="create-vendor-error">{errors.shopName}</span>}
            </div>

            {/* Owner Name */}
            <div className="create-vendor-form-group">
              <label htmlFor="ownerName" className="create-vendor-label">
                Owner Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="ownerName"
                name="ownerName"
                value={formData.ownerName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className={`create-vendor-input ${errors.ownerName ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.ownerName && <span className="create-vendor-error">{errors.ownerName}</span>}
            </div>

            {/* Email */}
            <div className="create-vendor-form-group">
              <label htmlFor="email" className="create-vendor-label">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className={`create-vendor-input ${errors.email ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.email && <span className="create-vendor-error">{errors.email}</span>}
            </div>

            {/* Phone */}
            <div className="create-vendor-form-group">
              <label htmlFor="phone" className="create-vendor-label">
                Phone Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                className={`create-vendor-input ${errors.phone ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.phone && <span className="create-vendor-error">{errors.phone}</span>}
            </div>

            {/* Shop Address */}
            <div className="create-vendor-form-group create-vendor-form-group-full">
              <label htmlFor="shopAddress" className="create-vendor-label">
                Shop Address <span className="required">*</span>
              </label>
              <textarea
                id="shopAddress"
                name="shopAddress"
                value={formData.shopAddress}
                onChange={handleInputChange}
                placeholder="Enter your shop address"
                className={`create-vendor-textarea ${errors.shopAddress ? 'error' : ''}`}
                rows="3"
                disabled={isSubmitting}
              />
              {errors.shopAddress && <span className="create-vendor-error">{errors.shopAddress}</span>}
            </div>

            {/* Latitude */}
            <div className="create-vendor-form-group">
              <label htmlFor="latitude" className="create-vendor-label">
                Latitude (optional)
              </label>
              <input
                type="number"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                placeholder="e.g. 28.6139"
                step="any"
                className={`create-vendor-input ${errors.latitude ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.latitude && <span className="create-vendor-error">{errors.latitude}</span>}
            </div>

            {/* Longitude */}
            <div className="create-vendor-form-group">
              <label htmlFor="longitude" className="create-vendor-label">
                Longitude (optional)
              </label>
              <input
                type="number"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                placeholder="e.g. 77.2090"
                step="any"
                className={`create-vendor-input ${errors.longitude ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.longitude && <span className="create-vendor-error">{errors.longitude}</span>}
            </div>

            {/* Use Current Location Button */}
            <div className="create-vendor-form-group create-vendor-form-group-full">
              <button
                type="button"
                onClick={getCurrentLocation}
                className="create-vendor-location-btn"
                disabled={isSubmitting}
              >
                <MapPin size={16} />
                Use Current Location
              </button>
            </div>

            {/* Password */}
            <div className="create-vendor-form-group">
              <label htmlFor="password" className="create-vendor-label">
                Password <span className="required">*</span>
              </label>
              <div className="create-vendor-password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a strong password"
                  className={`create-vendor-input ${errors.password ? 'error' : ''}`}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="create-vendor-password-toggle"
                  disabled={isSubmitting}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <span className="create-vendor-error">{errors.password}</span>}
            </div>

            {/* Confirm Password */}
            <div className="create-vendor-form-group">
              <label htmlFor="confirmPassword" className="create-vendor-label">
                Confirm Password <span className="required">*</span>
              </label>
              <div className="create-vendor-password-container">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Re-enter your password"
                  className={`create-vendor-input ${errors.confirmPassword ? 'error' : ''}`}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="create-vendor-password-toggle"
                  disabled={isSubmitting}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && <span className="create-vendor-error">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div className="create-vendor-form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="create-vendor-btn create-vendor-btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-vendor-btn create-vendor-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Vendor Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVendorModal;
